import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormBase } from '../../../_classes/form-base.class';
import ICalendar from '../../../_interfaces/ICalendar';
import { Observable } from 'rxjs';
import { SessionService } from '../../../_services/session/session.service';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CalendarService } from '../../../_services/calendars/calendar.service';
import { ValidatorService } from '../../../_services/validators/validator.service';
import { DeleteButtonCalendarComponent } from '../../delete-buttons/delete-button-calendar/delete-button-calendar.component';
import { EResources } from '../../../_resources/EResources';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import IUser from '../../../_interfaces/IUser';
import { SitesService } from '../../../_services/sites/sites.service';

@Component({
  selector: 'app-form-calendar',
  imports: [
    TranslatePipe,

    FormsModule,
    CommonModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    MultiSelectModule,
    DeleteButtonCalendarComponent,
  ],
  templateUrl: './form-calendar.component.html',
  styleUrl: './form-calendar.component.scss',
})
export class FormCalendarComponent extends FormBase<ICalendar> {
  public submitting = false;
  public professionals: IUser[] = [];
  public loadingProfessionals = false;
  public professionalSelectionLimit = 10;

  constructor(
    public session: SessionService,
    public messageService: MessageService,
    public translate: TranslateService,
    public calendarService: CalendarService,
    public validatorService: ValidatorService,
    private sitesService: SitesService,
  ) {
    super(session, messageService, translate);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadProfessionals();
  }

  private normalizeUsersId(value: unknown): number | null {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed <= 0) {
      return null;
    }
    return parsed;
  }

  private loadProfessionals(): void {
    const siteId = Number(this.affectedSite?.id || 0);
    if (!this.customers_id || !siteId) {
      this.professionals = [];
      return;
    }

    this.loadingProfessionals = true;
    this.sitesService.getUsers(this.customers_id, siteId).subscribe({
      next: (users) => {
        this.professionals = (users || []).map((user) => ({
          ...user,
          fullName: user.surname ? `${user.name} ${user.surname}` : user.name,
        })) as any;

        const selectedUsersId = this.normalizeUsersId(this.payload?.users_id);
        const fallbackUsers = [
          ...(this.payload?.defaultProfessional?.id
            ? [this.payload.defaultProfessional]
            : []),
          ...((this.payload?.professionals || []) as IUser[]),
        ].filter(Boolean);

        for (const fallback of fallbackUsers) {
          const existsInSite = this.professionals.some(
            (user) => Number(user?.id || 0) === Number(fallback?.id || 0),
          );
          if (!existsInSite && fallback?.id) {
            this.professionals = [
              ...this.professionals,
              {
                ...fallback,
                fullName: fallback.surname
                  ? `${fallback.name} ${fallback.surname}`
                  : fallback.name,
              } as any,
            ];
          }
        }

        if (
          selectedUsersId &&
          !Array.isArray(this.payload?.professional_users_ids)
        ) {
          this.payload.professional_users_ids = [selectedUsersId];
        } else if (!Array.isArray(this.payload?.professional_users_ids)) {
          this.payload.professional_users_ids = (this.payload?.professionals || [])
            .map((user) => Number(user?.id || 0))
            .filter((id) => Number.isInteger(id) && id > 0);
        }

        this.loadingProfessionals = false;
      },
      error: () => {
        this.professionals = [];
        this.loadingProfessionals = false;
      },
    });
  }

  clear(): void {
    this.payload = {
      name: undefined,
      precision: 15,
      users_id: null,
      professional_users_ids: [],
    };
  }

  onDefaultProfessionalChanged(users_id: number | null): void {
    const selectedUsersId = this.normalizeUsersId(users_id);
    if (!selectedUsersId) {
      return;
    }

    const current = Array.isArray(this.payload?.professional_users_ids)
      ? this.payload.professional_users_ids
      : [];
    if (!current.includes(selectedUsersId)) {
      this.payload.professional_users_ids = [...current, selectedUsersId];
    }
  }

  generatePayload(): ICalendar {
    return {
      name: this.payload.name,
      precision: this.payload.precision,
      users_id: this.normalizeUsersId(this.payload?.users_id),
      professional_users_ids: Array.from(
        new Set(
          [
            ...(this.payload?.professional_users_ids || []).map((id) => Number(id || 0)),
            ...(this.normalizeUsersId(this.payload?.users_id)
              ? [this.normalizeUsersId(this.payload?.users_id)]
              : []),
          ].filter((id) => Number.isInteger(id) && id > 0),
        ),
      ),
    };
  }
  getResourceName(): string {
    return EResources.CALENDARS;
  }
  updateFn(payload: ICalendar): Observable<any> {
    payload.id = this.id;
    return this.calendarService.update(
      this.customers_id,
      this.affectedSite.id,
      payload
    );
  }
  createFn(payload: ICalendar): Observable<any> {
    return this.calendarService.create(
      this.customers_id,
      this.affectedSite.id,
      payload
    );
  }
  properties(): string[] {
    return ['name', 'precision', 'users_id', 'professional_users_ids'];
  }
  validate(): Promise<void> {
    return this.validatorService.calendar(this.payload);
  }

  override async execute(): Promise<void> {
    this.submitting = true;
    try {
      await super.execute();
    } finally {
      this.submitting = false;
    }
  }
}
